import { assertWriteLanded } from "@/lib/scheduledTaskEdit";

/**
 * Delete one chat thread, permanently.
 *
 * Deliberately FREE, not Pro. Two reasons, both load-bearing:
 *
 *   1. "Delete my account" in Settings is ungated for Apple 5.1.1(v)
 *      compliance. Paywalling the *granular* delete while the nuclear one
 *      is free and legally required is incoherent — the only thing a gate
 *      would buy is forcing a free user to nuke everything to remove one
 *      thread.
 *   2. Free accounts see only their 5 most recent chats (see `openDrawer`
 *      in Index.tsx). A Pro gate would mean a free user staring at 5 chats
 *      she cannot remove any of. That is monetising privacy, not a feature.
 *
 * Guardrail 9's "anything that RUNS ON A SCHEDULE is Pro by default" does
 * not apply: this only ever runs when she taps it. See #1442 for the
 * separate, still-open question of an automatic retention window — that one
 * IS scheduled and is a different decision.
 *
 * `messages.conversation_id` is `ON DELETE CASCADE` (migration
 * 20260520131800), so the thread's messages go with the row. There is no
 * second delete to issue and no orphan to sweep.
 *
 * Guardrail 16: supabase-js RESOLVES `{ data, error }` on an RLS refusal,
 * and an RLS-filtered delete matching zero rows returns 200 with
 * `error === null` and no rows. `if (error)` alone cannot see that, so we
 * ask for the deleted ids back and assert they arrived. Without the
 * `.select("id")` this function would report success over a no-op.
 */
export async function deleteConversation(
  client: {
    from: (table: string) => {
      delete: () => {
        eq: (col: string, val: string) => {
          select: (cols: string) => PromiseLike<{ data: unknown; error: unknown }>;
        };
      };
    };
  },
  conversationId: string,
): Promise<void> {
  const { data, error } = await client
    .from("conversations")
    .delete()
    .eq("id", conversationId)
    .select("id");

  if (error) throw error;
  assertWriteLanded(data, "chat");
}
